/**
 * 线路班次模块接口
 * @description 线路、班次、站点、区域等相关接口
 */

import axios from "@/utils/request";
import Api from "../api";
import * as Request from "../request.type";
import * as ResponseData from "../response.type";
import {
  PartialFields,
  BasePaginationParams,
  PaginationResponse,
} from "../common.type";

/**
 * 获取运营公司列表
 */
export const Http_postOperateCompanyList = async (levelType: number) => {
  return await axios.post(Api.POST_OPERATE_COMPANY_LIST, {
    levelType,
  });
};

/**
 * 查询路线出发城市区域列表
 */
export const Http_getDepartureRouteCityAreaList = async () => {
  return await axios.get<ResponseData.getDepartureRouteCityAreaListData[]>(
    Api.GET_DEPARTURE_ROUTE_CITY_AREA_LIST
  );
};

/**
 * 通过出发城市区域信息查询路线到达城市区域列表
 */
export const Http_postDepartureRouteCityAreaList = async (
  params: Request.PostDepartureRouteCityAreaListParams
) => {
  return await axios.post<ResponseData.getDepartureRouteCityAreaListData[]>(
    Api.GET_DEPARTURE_ROUTE_CITY_AREA_LIST,
    params
  );
};

/**
 * 添加班次接口
 * @param params
 * @returns
 */
export const Http_postAddLine = async (lineNos: number[]) => {
  return await axios.post<ResponseData.LineData[]>(Api.POST_ADD_LINE, {
    lineNos,
  });
};

/**
 * 查询有效区域列表
 */
export const Http_getValidAreaList = async () => {
  return await axios.get<ResponseData.ValidAreaListItem[]>(
    Api.GET_VALID_AREA_LIST,
    {
      params: {
        status: 1,
      },
    }
  );
};

/**
 * 查询所有站点列表
 */
export const Http_getAllStationList = async () => {
  return await axios.get<ResponseData.StationListItem[]>(Api.GET_STATION_LIST);
};

/**
 * 获取区域下有效站点列表
 */
export const Http_getValidStationList = async ({
  areaId,
}: {
  areaId: string | number;
}) => {
  return await axios.get<ResponseData.StationListItem[]>(
    `${Api.GET_VALID_STATION_LIST}/${areaId}`
  );
};

/**
 * 新增站点
 */
export const Http_postStationAdd = async (params: Request.StationAddParams) => {
  return await axios.post(Api.POST_STATION_ADD, params);
};

/**
 * 搜索运营公司（按名称）
 */
export const Http_searchOperateCompany = async (name?: string) => {
  return await axios.post(Api.POST_OPERATE_COMPANY_LIST, {
    name: name || "",
  });
};

/**
 * 更新站点信息（新增/编辑）
 */
export const Http_postStationUpdate = async (
  params: Request.StationUpdateParams
) => {
  return await axios.post(Api.POST_STATION_UPDATE, params);
};

/**
 * 获取站点信息详情
 */
export const Http_getStationInfo = async (
  params: Request.StationInfoParams
) => {
  return await axios.get<ResponseData.StationInfoData>(Api.GET_STATION_INFO, {
    params,
  });
};

/**
 * 根据城市获取区域列表（往返站点用）
 */
export const Http_getAreaSelect = async (params: Request.AreaSelectParams) => {
  return await axios.get(Api.GET_AREA_SELECT, { params });
};

/**
 * 获取限制车票规则详情
 */
export const Http_getRestrictedTicketRuleDetail = async (params: {
  /** 规则 ID */
  id: number;
}) => {
  return await axios.get<ResponseData.RestrictedTicketRuleItem>(
    `${Api.GET_RESTRICTED_TICKET_RULE_DETAIL}/${params.id}`
  );
};

/**
 * 限制车票使用更改或新增
 */
export const Http_postRestrictedTicketRuleEdit = async (
  params: {
    /** 规则ID 有 ID 为更改、无 ID 为新增 */
    id?: number;
  } & ResponseData.RestrictedTicketRuleItem
) => {
  return await axios.post<any>(Api.POST_RESTRICTED_TICKET_RULE_EDIT, params);
};

/**
 * 获取线路信息列表
 */
export const Http_getLineInfoList = async (
  /** 路线方向，1-南行，2-北行。不传表示南北行都查 */
  routeDirection?: 1 | 2,
  carType?: undefined | 1 | 2
) => {
  return await axios.get<ResponseData.LineInfoItem[]>(Api.GET_LINE_INFO_LIST, {
    params: {
      routeDirection,
      carType,
    },
  });
};

// ==================== 限制座位预警模块接口 ====================

/**
 * 获取预警信息分页列表
 */
export const Http_getWarningMessagePageList = async (
  params: Request.GetWarningMessagePageListParams
) => {
  return await axios.get<{
    list: ResponseData.WarningMessageItem[];
    total: number;
    pages: number;
  }>(Api.GET_WARNING_MESSAGE_PAGE_LIST, {
    params,
  });
};

/**
 * 获取预警规则分页列表
 */
export const Http_getWarningRulePageList = async (
  params: Request.GetWarningRulePageListParams
) => {
  return await axios.get<{
    list: ResponseData.WarningRuleItem[];
    total: number;
    pages: number;
  }>(Api.GET_WARNING_RULE_PAGE_LIST, {
    params,
  });
};

/**
 * 关闭预警信息（批量）
 */
export const Http_postWarningMessageClose = async (
  params: Request.CloseWarningMessageParams
) => {
  return await axios.post<ResponseData.CloseWarningMessageData>(
    Api.POST_WARNING_MESSAGE_CLOSE,
    params
  );
};

/**
 * 获取接驳规则列表
 */
export const Http_getTransferRuleList = async (
  params: {
    /** 规则名称 */
    name?: string;
    /** 限制方案类型.1-指定不接驳 2-指定接驳 传空表示不限制 */
    type?: 1 | 2;
    /** 规则状态 1启用 0禁用 传空表示不限制 */
    status?: 0 | 1;
  } & BasePaginationParams
) => {
  return await axios.get<PaginationResponse<ResponseData.TransferRuleData>>(
    Api.GET_TRANSFER_RULE_LIST,
    { params }
  );
};

/**
 * 关闭预警信息（按ID关闭）
 */
export const Http_postWarningMessageCloseById = async (id: number) => {
  return await axios.post(`${Api.POST_WARNING_MESSAGE_CLOSE}/${id}`);
};

/**
 * 批量关闭预警信息
 */
export const Http_postWarningMessageCloseBatch = async (ids: number[]) => {
  return await axios.post(Api.POST_WARNING_MESSAGE_BATCH_CLOSE, {
    ids,
  });
};

/**
 * 修改预警规则状态
 */
export const Http_postWarningRuleChangeStatus = async (
  params: Request.ChangeWarningRuleStatusParams
) => {
  return await axios.post<ResponseData.ChangeWarningRuleStatusData>(
    Api.POST_WARNING_RULE_CHANGE_STATUS,
    params
  );
};

/**
 * 获取预警规则详情
 */
export const Http_getWarningRuleDetail = async (
  params: Request.GetWarningRuleDetailParams
) => {
  return await axios.get<ResponseData.WarningRuleDetailData>(
    `${Api.GET_WARNING_RULE_DETAIL}/${params.id}`
  );
};

/**
 * 新增预警规则
 */
export const Http_postWarningRuleAdd = async (
  params: Request.SaveWarningRuleParams
) => {
  return await axios.post<ResponseData.AddWarningRuleData>(
    Api.POST_WARNING_RULE_ADD,
    params
  );
};

/**
 * 更新预警规则
 */
export const Http_postWarningRuleUpdate = async (
  params: Request.SaveWarningRuleParams
) => {
  return await axios.post<ResponseData.UpdateWarningRuleData>(
    Api.POST_WARNING_RULE_UPDATE,
    params
  );
};

/**
 * 获取所有关口站点
 */
export const Http_getAllGateWayStation = async () => {
  return await axios.get<
    {
      /** 站点ID */
      id: number;
      /** 站点名称 */
      nameCn: string;
    }[]
  >(Api.GET_ALL_GATEWAY_STATION);
};

/**
 * 查询接驳规则详情
 */
export const Http_getTransferRuleDetail = async (id: string) => {
  return await axios.get<ResponseData.TransferRuleData>(
    Api.GET_TRANSFER_RULE_DETAIL,
    {
      params: {
        id,
      },
    }
  );
};

/**
 * 保存接驳规则
 */
export const Http_postTransferRuleSave = async (
  params: Omit<ResponseData.TransferRuleData, "id" | "detailList"> & {
    id?: number;
    detailList: ResponseData.TransferLineData[];
  }
) => {
  return await axios.post(
    params.id ? Api.POST_TRANSFER_RULE_UPDATE : Api.POST_TRANSFER_RULE_ADD,
    params
  );
};

/**
 * 获取接驳班次列表
 */
export const Http_getTransferLineList = async (params: {
  /** 限制行程类型 1-路线，2-行程，3-班次 */
  type: 1 | 2 | 3;
  /** 限制日期开始 */
  lineDateBegin: string;
  /** 限制日期结束 */
  lineDateEnd: string;
  /** 路线名称 */
  routeName?: string;
  /** 行程名称 */
  itineraryName?: string;
  /** 班次编码 */
  lineCode?: string;
}) => {
  return await axios.get<ResponseData.TransferLineData>(
    Api.GET_TRANSFER_LINE_LIST,
    { params }
  );
};

/**
 * 获取路线列表分页版本
 */
export const Http_getRouteList = async (
  params: Request.RouteQueryParams & BasePaginationParams
) => {
  return await axios.post<PaginationResponse<ResponseData.RouteListItem>>(
    Api.GET_ROUTE_LIST,
    params
  );
};

/**
 * 获取行程列表
 */
export const Http_getTripList = async () => {
  return await axios.get<ResponseData.TripListItem[]>(Api.GET_TRIP_LIST);
};
